/**
 * This is the entry point for Feature Hub App integration
 */
import React from 'react';
import { Store } from 'redux';
import { Provider } from 'react-redux';

import { FeatureAppDefinition, FeatureAppEnvironment } from '@feature-hub/core';
import { ReactFeatureApp } from '@feature-hub/react';

import I18nContextComponent from '@oneaudi/i18n-context';
import { defineConfigurationService } from '@oneaudi/vtp-configuration-service';

import {
  AudiStockcarsStoreServiceV1,
  defineAudiStockcarsStoreService,
} from '@oneaudi/stockcars-store-service';

import { OneGraphServiceV1 } from '@oneaudi/onegraph-service';
import { ApolloClient, OneGraphProvider } from '@oneaudi/onegraph-client';
import type { AuthServiceProviderV4 } from '@oneaudi/audi-auth-service';

import {
  CommonFeatureAppDependencies,
  createFilterSeoResolver,
  DynamicThemeProviderWrapper,
  FootnoteReferenceContextProvider,
  FootnoteServiceContextProvider,
  getDesignLibrary,
  initializeFeatureApp,
  SeoFilterValue,
  ServicesContextProvider,
  AuthContextProvider,
  MyAudiWishlistContextProvider,
} from '@oneaudi/vtp-shared';

import App from './FeatureApp';

export interface FeatureServiceDependencies extends CommonFeatureAppDependencies {
  readonly 'audi-stockcars-store-service': AudiStockcarsStoreServiceV1;
  readonly 'onegraph-service': OneGraphServiceV1;
  readonly 'vw:authService': AuthServiceProviderV4;
  // add PLP only services here.
}
export interface FeatureAppConfig {
  vehicleId?: string;
  contextServiceUrl?: string;
  contextId?: string;
  baseUrl?: string;
  assetUrl?: string;
}

export type CombinedConfig = FeatureAppConfig;

type AdditionalData = {
  seoFilters: SeoFilterValue[];
};

const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, FeatureServiceDependencies> = {
  dependencies: {
    featureServices: {
      'gfa:locale-service': '^1.0.0',
      'dbad:audi-i18n-service': '^1.0.0',
      'locale-service': '1.0.0',
      'layer-manager': '^2.5.0',
      'vtp-configuration-service': '^1.0.0',
      'audi:envConfigService': '1.0.0',
      'audi-stockcars-store-service': '1.0.0',
      'audi-footnote-reference-service': '^3.0.0',
      'audi-content-service': '^1.0.0',
      'onegraph-service': '^1.0.0',
      'audi-number-formatter-service': '^1.0.0',
      'vw:authService': '^4.0.0',
    },
    externals: {
      react: '^16.13.0 || ^17.0.2 || ^18.2.0',
      'styled-components': '*',
      '@oneaudi/onegraph-service': '*',
      ...getDesignLibrary({
        unifiedWebCommonVersion: '^1.13.0',
        unifiedWebAlphaCommonVersion: '1.9.0',
      }),
    },
  },
  optionalDependencies: {
    featureServices: {
      's2:logger': '^1.0.0',
      's2:async-ssr-manager': '^1.0.0',
      's2:server-request': '^1.0.0',
      'audi-tracking-service': '^2.0.0',
      's2:serialized-state-manager': '^1.0.0',
      'audi-footnote-service': '1.0.0',
      'notification-display-service': '^1.0.0',
      'page-info-service': '1.0.0',
      'gfa:layer-manager': '1.0.0',
    },
  },
  ownFeatureServiceDefinitions: [defineAudiStockcarsStoreService(), defineConfigurationService()],
  create: ({
    featureServices,
    config = {},
    baseUrl,
  }: FeatureAppEnvironment<FeatureServiceDependencies, FeatureAppConfig>) => {
    const loggerService = featureServices['s2:logger'];
    loggerService?.info('Feature App created.');
    const i18nService = featureServices['dbad:audi-i18n-service'];
    const localeService = featureServices['locale-service'];
    const stockcarsStoreService = featureServices['audi-stockcars-store-service'];
    const pageInfoService = featureServices['page-info-service'];
    const serverRequest = featureServices['s2:server-request'];
    const configurationService = featureServices['vtp-configuration-service'];
    const envConfigService = featureServices['audi:envConfigService'];

    // do not use destructuring here, as this breaks the ssr build.
    const initialization = initializeFeatureApp<
      FeatureServiceDependencies,
      FeatureAppConfig,
      AdditionalData
    >(featureServices, config, {
      seoFilters: createFilterSeoResolver(
        configurationService,
        envConfigService,
        serverRequest,
        pageInfoService,
        i18nService,
        localeService,
        loggerService
      ),
    });

    if (!config.assetUrl) {
      // eslint-disable-next-line
      config.assetUrl = `${baseUrl || ''}/static`;
    } else {
      // eslint-disable-next-line
      config.assetUrl = config.assetUrl.endsWith('/')
        ? config.assetUrl.slice(0, -1)
        : config.assetUrl;
    }

    const oneGraphClient: ApolloClient<unknown> =
      featureServices['onegraph-service'].getApolloClient();

    const footnoteReferenceService =
      featureServices['audi-footnote-reference-service']?.getDefaultScopeRefService();

    const authServiceProvider = featureServices['vw:authService'];
    const authService = authServiceProvider.register('myaudi');

    return {
      loadingPromise: initialization.loadingPromise?.featureAppInitializationPromise,
      render: () => (
        <>
          <DynamicThemeProviderWrapper>
            <Provider store={stockcarsStoreService.store.store as unknown as Store}>
              <ServicesContextProvider
                audiStockcarsStoreService={stockcarsStoreService}
                contextId={initialization.contextId!}
                featureAppConfig={config}
                additionalServices={featureServices}
                envConfig={initialization.envConfig}
              >
                <I18nContextComponent
                  i18nData={i18nService}
                  language={localeService.language}
                  // @ts-ignore
                  featureServices={featureServices}
                  scopes={[]}
                >
                  <FootnoteServiceContextProvider
                    footnoteService={featureServices['audi-footnote-service']}
                  >
                    <FootnoteReferenceContextProvider
                      footnoteReferenceServiceScopeManager={
                        featureServices['audi-footnote-reference-service']
                      }
                    >
                      <div data-fefa-custom-id={footnoteReferenceService.getConsumerId()}>
                        <OneGraphProvider client={oneGraphClient as ApolloClient<unknown>}>
                          {initialization.loadingPromise?.state.initialized && (
                            <AuthContextProvider authService={authService}>
                              <MyAudiWishlistContextProvider>
                                <App seoFilters={initialization.additionalData.seoFilters} />
                              </MyAudiWishlistContextProvider>
                            </AuthContextProvider>
                          )}
                        </OneGraphProvider>
                      </div>
                    </FootnoteReferenceContextProvider>
                  </FootnoteServiceContextProvider>
                </I18nContextComponent>
              </ServicesContextProvider>
            </Provider>
          </DynamicThemeProviderWrapper>
        </>
      ),
    };
  },
};
export default featureAppDefinition;
